import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { colors, CONSTANTS } from 'constants/index'
import { ContingencySection, FlatFeeSection, InputLabel, TimeAndExpenseSection, Typography } from 'components'
import { RadioGroup } from '@material-ui/core'
import Radio from '@material-ui/core/Radio'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useSelector } from 'react-redux'

const useStyles = makeStyles({
  section: {
    borderBottom: `1px solid ${colors.BASIC.COLOR[10]}`,
    marginBottom: 20,
  },
  formData: {
    width: '60%',
  },
  primaryInfo: {
    display: 'flex',
  },
  standardMargin: {
    marginBottom: 16,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: colors.BASIC.COLOR[0],
    padding: '10px 24px',
  },
})

const getFeeTypeLabel = (feeType, isAccountant) => {
  const feeTypes = CONSTANTS.FEE_TYPES(isAccountant)
  return feeTypes[feeType] ? feeTypes[feeType].label : ''
}

const getSection = (feeType, isAccountant, params) => {
  const feeTypes = CONSTANTS.FEE_TYPES(isAccountant)
  switch (feeType) {
    case feeTypes['1'].key:
      return <FlatFeeSection {...params} />
    case feeTypes['2'].key:
    case feeTypes['4'].key:
      return <ContingencySection {...params} />
    case feeTypes['3'].key:
      return <TimeAndExpenseSection {...params} />
    default:
      return null
  }
}

const SectionSubtitle = ({ feeType, isAccountant }) => {
  const classes = useStyles()
  const feeTypes = CONSTANTS.FEE_TYPES(isAccountant)
  switch (feeType) {
    case feeTypes['1'].key:
      return isAccountant ? (
        <Typography variant="subtitle2" className={classes.standardMargin}>
          Flat fee agreements can be paid incrementally or all at once, according to the agreement between service
          provider and client. Use the Invoice Date field to set the schedule. Payments may not exceed the total amount
          of the agreed flat fee. If additional work not included in the flat fee is requested by the client, the
          service provider may need to create a new Project/File or submit as Other Expense/Charges for such work.
        </Typography>
      ) : (
        <Typography variant="subtitle2" className={classes.standardMargin}>
          Flat fee agreements can be paid incrementally or all at once, according to the agreement between lawyer and
          client. Use the Invoice Date field to set the schedule. Payments may not exceed the total amount of the agreed
          flat fee. If additional work not included in the flat fee is requested by the client, the lawyer may need to
          create a new case/matter for such work.
        </Typography>
      )
    case feeTypes['2'].key:
      return (
        <Typography variant="subtitle2" className={classes.standardMargin}>
          Under this fee type, activities for time and expenses can be generated in the activities table. XIRA can
          optionally generate an invoice for expenses if the option "Expense invoicing" is selected. Any other invoice
          needs to be manually generated and you must inform XIRA.
        </Typography>
      )
    case feeTypes['3'].key:
      return (
        <Typography variant="subtitle2" className={classes.standardMargin}>
          Under this fee type, activities can be generated in the activities table. Client will be charged for the time
          and expenses as noted in the activity table.
        </Typography>
      )
    case feeTypes['4'].key:
      return (
        <Typography variant="subtitle2" className={classes.standardMargin}>
          Recurring billing allows you to set up automatic, periodic payments with a fixed amount and due dates. Once
          created, clients are notified and prompted to authorize the recurring payments. The system then automatically
          charges their designated payment method on the selected date intervals. Any additional expenses or charges
          incurred during the period will be added to the recurring invoice.
        </Typography>
      )
    default:
      return null
  }
}

const CaseFeeSettingsSelector = props => {
  const classes = useStyles()
  const { caseData, mode, onChange, setErrors, isInitial, children } = props
  const currentUser = useSelector(state => state.auth.currentUser)
  const isAccountant = currentUser?.subrole === 'accountant'
  const feeTypes = CONSTANTS.FEE_TYPES(isAccountant)

  const onFeeTypeChange = (event, value) => {
    onChange({
      fee_type: parseInt(value, 10),
      invoicing: null,
      expense_invoicing: false,
      invoice_frequency: null,
      invoicing_sections: [
        {
          invoice_amount: '',
          invoice_date: '',
        },
      ],
      amount: '',
    })
    if (setErrors) {
      setErrors({
        invoicing: '',
        expense_invoicing: '',
        invoice_frequency: '',
        invoicing_sections: [],
        amount: '',
      })
    }
  }

  return (
    <div>
      {!isInitial && <InputLabel>{isAccountant ? 'Fee settings' : 'Case fee settings'}</InputLabel>}
      {!isInitial &&
        (mode === CONSTANTS.MODE.ADD ? (
          <RadioGroup value={caseData.fee_type} onChange={onFeeTypeChange} className={classes.radioGroup}>
            <FormControlLabel value={3} control={<Radio color="primary" />} label={feeTypes['3'].label} />
            <FormControlLabel value={1} control={<Radio color="primary" />} label={feeTypes['1'].label} />
            {!isAccountant ? (
              <FormControlLabel value={2} control={<Radio color="primary" />} label={feeTypes['2'].label} />
            ) : (
              <FormControlLabel value={4} control={<Radio color="primary" />} label={feeTypes['4'].label} />
            )}
          </RadioGroup>
        ) : (
          <InputLabel classes={{ root: classes.standardMargin }}>
            {getFeeTypeLabel(caseData.fee_type, isAccountant)}
          </InputLabel>
        ))}
      {!isInitial && <SectionSubtitle feeType={caseData.fee_type} isAccountant={isAccountant} />}
      {children}
      {!isInitial && getSection(caseData.fee_type, isAccountant, props)}
    </div>
  )
}

export default CaseFeeSettingsSelector
